import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Reveal from './Reveal';

/**
 * MeetTheCreator
 * Personal, editorial-style section introducing Simon.
 * Uses a real portrait + script-style signature.
 */

const CREATOR_PORTRAIT =
  'https://customer-assets-wrfwihn1.emergentagent.net/job_slideshow-studio-5/artifacts/zju2o1oj_me%20suit.png';

const MeetTheCreator = () => {
  return (
    <section className="py-28 bg-black relative overflow-hidden" data-testid="meet-the-creator">
      {/* Soft amber glow behind portrait */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 25% 50%, rgba(201, 162, 39, 0.12), transparent 55%)',
        }}
      />

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
          {/* Portrait */}
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-amber-500/20 via-amber-600/10 to-transparent rounded-[2rem] blur-2xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl aspect-[4/5] max-w-md mx-auto lg:mx-0">
                <img
                  src={CREATOR_PORTRAIT}
                  alt="Simon - Founder of Visual Creationz"
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
                {/* Editorial vignette */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-xs uppercase tracking-[0.3em] text-amber-400/90 mb-1">
                    Founder · Editor · Storyteller
                  </div>
                  <div className="text-white font-semibold">Simon</div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Copy */}
          <Reveal delay={1} className="lg:col-span-7 space-y-6">
            <div>
              <p className="uppercase tracking-[0.3em] text-xs text-amber-400 mb-4">
                Meet the Creator
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Hi, I'm <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Simon</span>.
              </h2>
            </div>

            <div className="space-y-5 text-lg text-gray-300 leading-relaxed max-w-xl">
              <p>
                I created Visual Creationz because I believe life's most precious memories deserve more than being forgotten on a phone or computer.
              </p>
              <p>
                Every project is personally edited with care, creativity and a real attention to detail - transforming your photos, videos and music into cinematic memories you'll treasure forever.
              </p>
            </div>

            {/* Handwritten signature */}
            <div className="pt-4">
              <div className="signature-font text-5xl md:text-6xl text-amber-400 leading-none">
                Simon
              </div>
              <div className="text-xs uppercase tracking-[0.3em] text-gray-500 mt-1">
                Founder, Visual Creationz
              </div>
            </div>

            <div className="pt-4">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium transition-colors group"
                data-testid="meet-creator-learn-more"
              >
                Read My Story
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default MeetTheCreator;
